import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { useTheme, type Theme } from '@mui/material/styles'

export type RsvpCounts = {
  pending: number
  accepted: number
  waitlisted: number
  declined: number
  attended: number
}

type Props = {
  counts: RsvpCounts
  onCardClick?: (statusKey: string) => void
}

export let buildRsvpStatCols = (
  theme: Theme,
  counts: RsvpCounts,
  prefix = '',
) => [
  {
    label: `${prefix}Pending RSVP`,
    value: counts.pending,
    color: theme.palette.analyticsColors.recordedCourses,
    statusKey: 'invited',
  },
  {
    label: `${prefix}Accepted`,
    value: counts.accepted,
    color: theme.palette.info.main,
    statusKey: 'accepted',
  },
  {
    label: `${prefix}Waitlisted`,
    value: counts.waitlisted,
    color: theme.palette.warning.main,
    statusKey: 'waitlisted',
  },
  {
    label: `${prefix}Declined`,
    value: counts.declined,
    color: theme.palette.error.main,
    statusKey: 'declined',
  },
  {
    label: `${prefix}Attended`,
    value: counts.attended,
    color: theme.palette.success.main,
    statusKey: 'attended',
  },
]

let RsvpStatCards = ({ counts, onCardClick }: Props) => {
  let theme = useTheme()
  let statCols = buildRsvpStatCols(theme, counts, 'Total ')

  return (
    <Box className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 my-2">
      {statCols.map((col) => (
        <Box
          key={col.label}
          className="flex flex-col justify-end gap-2 p-4 rounded"
          role={onCardClick ? 'button' : undefined}
          tabIndex={onCardClick ? 0 : undefined}
          onClick={onCardClick ? () => onCardClick(col.statusKey) : undefined}
          onKeyDown={
            onCardClick
              ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onCardClick(col.statusKey)
                  }
                }
              : undefined
          }
          sx={{
            background: '#1e2124',
            border: '1px solid rgba(255,255,255,.12)',
            minHeight: 104,
            cursor: onCardClick ? 'pointer' : 'default',
            '&:hover': onCardClick
              ? { border: '1px solid rgba(255,255,255,.28)' }
              : undefined,
            '&:focus-visible': onCardClick
              ? {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: '2px',
                }
              : undefined,
          }}
        >
          <Typography
            sx={{
              fontSize: 28,
              fontWeight: 600,
              color: col.color,
              lineHeight: 1,
            }}
          >
            {col.value.toLocaleString()}
          </Typography>
          <Typography
            className="uppercase"
            sx={{
              fontSize: 12,
              color: 'rgba(255,255,255,.55)',
              letterSpacing: '0.05em',
              lineHeight: 1.3,
            }}
          >
            {col.label}
          </Typography>
        </Box>
      ))}
    </Box>
  )
}

export default RsvpStatCards
